import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import {
	getCareTeam,
	getNotes,
	addNote,
	getPatientADTs,
	getCCDA,
	getDischargeSummary,
	addPatientToCampaigns,
} from './api';
import {
	loadProviders,
	createTextaVisit,
	isNumberMobile,
} from '../tracking-board/api';
import AdtViewer from './AdtViewer';
import PatientSearch from './PatientSearch';
import CareTeamModal from './CareTeamModal';
import TextaVisitModal from '../tracking-board/TextaVisitModal';
import NotesModal from './NotesModal';
import PatientADTModal from './PatientADTModal';
import {
	CCDAModal,
	AdditionalDetailsModal,
	AddToCampaignModal,
} from './components';

import './styles.scss';

const AdtViewWrapper = (props) => {
	const hasCampaigns = !!props.user.get('provider_practice').care_campaign_ind;

	const [isCareTeamModalOpen, setCareTeamModalOpen] = useState(false);
	const [isLoadingCareTeam, setLoadingCareTeam] = useState(false);
	const [careTeam, setCareTeam] = useState();
	const [selectedAdt, setSelectedAdt] = useState({});
	const [isTAVModalOpen, setTAVModalOpen] = useState(false);
	const [tavProviders, setTavProviders] = useState([]);
	const [selectedPatient, setSelectedPatient] = useState();
	const [isNotesModalOpen, setNotesModalOpen] = useState(false);
	const [notes, setNotes] = useState();
	const [isPatientADTModalOpen, setPatientADTModalOpen] = useState(false);
	const [patientADTs, setPatientADTs] = useState();
	const [isCCDAModalOpen, setCCDAModalOpen] = useState(false);
	const [ccda, setCCDA] = useState(null);
	const [dischargeSummary, setDischargeSummary] = useState(null);
	const [isAdditionalDetailsModalOpen, setAdditionalDetailsModalOpen] =
		useState(false);
	const [isAddToCampaignModalOpen, setAddToCampaignModalOpen] = useState(false);
	const [phoneData, setPhoneData] = useState();
	const [isAddingToCampaign, setAddingToCampaign] = useState(false);

	const clearSelectedAdt = () => {
		setSelectedAdt({});
	};

	useEffect(() => {
		loadProviders(props.user.get('id')).then(({ data }) =>
			setTavProviders(data.status)
		);
	}, []);

	useEffect(() => {
		if (!isCareTeamModalOpen) {
			clearSelectedAdt();
		}
	}, [isCareTeamModalOpen]);

	useEffect(() => {
		if (!isTAVModalOpen) {
			clearSelectedAdt();
		}
	}, [isTAVModalOpen]);

	useEffect(() => {
		if (!isNotesModalOpen) {
			clearSelectedAdt();
		}
	}, [isNotesModalOpen]);

	useEffect(() => {
		if (!isPatientADTModalOpen) {
			clearSelectedAdt();
		}
	}, [isPatientADTModalOpen]);

	useEffect(() => {
		if (!isAdditionalDetailsModalOpen) {
			clearSelectedAdt();
		}
	}, [isAdditionalDetailsModalOpen]);

	useEffect(() => {
		if (!isAddToCampaignModalOpen) {
			clearSelectedAdt();
		}
	}, [isAddToCampaignModalOpen]);

	const handleShowCareTeam = (adt) => {
		setCareTeamModalOpen(true);
		setLoadingCareTeam(true);
		setCareTeam([]);
		setSelectedAdt(adt);
		getCareTeam(props.user.get('id'), adt.id)
			.then(({ data }) => {
				if (data.status && Array.isArray(data.status)) {
					if (data.status.length > 0) {
						const records = data.status.reduce((prev, curr) => {
							prev.push(...curr.records);
							return prev;
						}, []);
						setLoadingCareTeam(false);
						setCareTeam(records);
					}
				}
				setLoadingCareTeam(false);
			})
			.catch(() => {
				props.showNotification({
					message: 'Unable to load Care Team!',
					status: 'danger',
					timeout: 10000,
				});
				setCareTeamModalOpen(false);
				setLoadingCareTeam(false);
				setCareTeam([]);
			});
	};

	const handleOpenInstavisit = (patient) => {
		setSelectedPatient(patient);
		setTAVModalOpen(true);
	};

	const handleCreateInstavisit = async (visitData) => {
		try {
			const { data } = await createTextaVisit(props.user.get('id'), visitData);
			setTAVModalOpen(false);
			if (data && data.cb === 'visit created') {
				props.showNotification({
					status: 'success',
					message: 'Text-a-Visit created. Patient has been notified.',
				});
			} else {
				props.showNotification({
					message: 'Unable to create Text-a-Visit!',
					status: 'danger',
					timeout: 10000,
				});
			}
		} catch (err) {
			props.showNotification({
				message: 'Unable to create Text-a-Visit!',
				status: 'danger',
				timeout: 10000,
			});
		}
	};

	const handleOpenNotesModal = async (selectedAdt) => {
		const userId = props.user.get('id');
		try {
			const { data } = await getNotes(userId, selectedAdt.id);
			if (data && data.cb && data.cb.acrs_result) {
				setNotesModalOpen(true);
				setSelectedAdt(selectedAdt);
				setNotes(data.cb.acrs_result);
			} else {
				setNotesModalOpen(true);
				setSelectedAdt(selectedAdt);
			}
		} catch (e) {
			// console.log(e)
		}
	};

	const handleCloseNotesModal = () => {
		setNotesModalOpen(false);
	};

	const handleSaveNote = async (note) => {
		const userId = props.user.get('id');
		try {
			await addNote(userId, selectedAdt.id, note);
		} catch (e) {
			//
		}
		setNotesModalOpen(false);
	};

	const handleShowPatientADTs = (adt) => {
		if (adt) {
			setSelectedAdt(adt);
			getPatientADTs(
				props.user.get('id'),
				adt.patient_first_name,
				adt.patient_last_name,
				adt.patient_dob,
				adt.patient_gender,
				adt.patient_city
			).then(({ data }) => {
				if (data && data.status) {
					setPatientADTs(data.status);
					setPatientADTModalOpen(true);
				}
			});
		}
	};

	const handleShowCCDA = (adtId, showFull = true) => {
		if (showFull) {
			getCCDA(props.user.get('id'), adtId).then(({ data }) => {
				if (data && data.cb) {
					setCCDA(data.cb);
					setDischargeSummary(null);
					setPatientADTModalOpen(false);
					setCCDAModalOpen(true);
				}
			});
		} else {
			getDischargeSummary(props.user.get('id'), adtId).then(({ data }) => {
				setCCDA(null);
				setDischargeSummary(data);
				setPatientADTModalOpen(false);
				setCCDAModalOpen(true);
			});
		}
	};

	const handleShowAdditionalDetails = (adt) => {
		setSelectedAdt(adt);
		setAdditionalDetailsModalOpen(true);
	};

	const handleShowAddToCampaignModal = (adt) => {
		setSelectedAdt(adt);
		setAddToCampaignModalOpen(true);
	};

	const handleVerifyMobileNumber = async (number) => {
		const userId = props.user.get('id');
		const phoneData = await isNumberMobile(userId, number);
		setPhoneData(phoneData);
	};

	const handleAddPatientToCampaigns = async (data) => {
		const userId = props.user.get('id');
		setAddingToCampaign(true);
		try {
			await addPatientToCampaigns(userId, data);
			if (data.campaign_demographic_id) {
				props.showNotification({
					message: 'Care Campaigns Updated',
					status: 'success',
				});
			} else {
				props.showNotification({
					message: 'Patient added to chosen Care Campaigns',
					status: 'success',
				});
			}
		} catch (e) {
			props.showNotification({
				message: 'Unable to add/update Care Campaigns!',
				status: 'danger',
				timeout: 10000,
			});
		}
		setAddToCampaignModalOpen(false);
		setAddingToCampaign(false);
		clearSelectedAdt();
	};

	return (
		<React.Fragment>
			{props.showPatientSearch ? (
				<PatientSearch
					{...props}
					onShowCareTeam={handleShowCareTeam}
					onOpenInstavisit={handleOpenInstavisit}
					onShowNotes={handleOpenNotesModal}
					onShowPatientADTs={handleShowPatientADTs}
					selectedAdt={selectedAdt}
					onShowCCDA={handleShowCCDA}
					hasCampaigns={hasCampaigns}
					onOpenAddToCampaign={handleShowAddToCampaignModal}
					isCampaignModalOpen={isAddToCampaignModalOpen}
					onShowAdditionalDetails={handleShowAdditionalDetails}
				/>
			) : (
				<AdtViewer
					{...props}
					onShowCareTeam={handleShowCareTeam}
					onOpenInstavisit={handleOpenInstavisit}
					onShowNotes={handleOpenNotesModal}
					onShowPatientADTs={handleShowPatientADTs}
					selectedAdt={selectedAdt}
					onShowCCDA={handleShowCCDA}
					onShowAdditionalDetails={handleShowAdditionalDetails}
					hasCampaigns={hasCampaigns}
					onOpenAddToCampaign={handleShowAddToCampaignModal}
					isCampaignModalOpen={isAddToCampaignModalOpen}
				/>
			)}
			<CareTeamModal
				isOpen={isCareTeamModalOpen}
				onClose={() => setCareTeamModalOpen(false)}
				isLoading={isLoadingCareTeam}
				careTeam={careTeam}
				adt={selectedAdt}
			/>
			<TextaVisitModal
				isOpen={isTAVModalOpen}
				onCancel={() => setTAVModalOpen(false)}
				providers={tavProviders}
				onSubmit={handleCreateInstavisit}
				onVerifyMobileNumber={handleVerifyMobileNumber}
				phoneData={phoneData}
				{...selectedPatient}
			/>
			<NotesModal
				isOpen={isNotesModalOpen}
				onClose={handleCloseNotesModal}
				notes={notes}
				onSave={handleSaveNote}
			/>
			<PatientADTModal
				isOpen={isPatientADTModalOpen}
				onClose={() => setPatientADTModalOpen(false)}
				patientADTs={patientADTs}
				currentAdt={selectedAdt}
				onShowCCDA={handleShowCCDA}
			/>
			<CCDAModal
				isOpen={isCCDAModalOpen}
				onClose={() => setCCDAModalOpen(false)}
				ccda={ccda}
				userId={props.user.get('id')}
				dischargeSummary={dischargeSummary}
			/>
			<AdditionalDetailsModal
				isOpen={isAdditionalDetailsModalOpen}
				onClose={() => setAdditionalDetailsModalOpen(false)}
				userId={props.user.get('id')}
				adt={selectedAdt}
			/>
			<AddToCampaignModal
				isOpen={isAddToCampaignModalOpen}
				onClose={() => setAddToCampaignModalOpen(false)}
				adt={selectedAdt}
				onAdd={handleAddPatientToCampaigns}
				isAdding={isAddingToCampaign}
				onVerifyMobileNumber={handleVerifyMobileNumber}
				phoneData={phoneData}
				userId={props.user.get('id')}
			/>
		</React.Fragment>
	);
};

AdtViewWrapper.propTypes = {
	showPatientSearch: PropTypes.bool,
	user: PropTypes.object,
	showNotification: PropTypes.func.isRequired,
};

AdtViewWrapper.defaultProps = {
	showNotification: () => {},
};

export default AdtViewWrapper;
